import {tva} from "@gluestack-ui/nativewind-utils/tva";
import {Circle} from "rc-progress";
import type {FC} from "react";
import {memo} from "react";
import type {DimensionValue} from "react-native";
import {AnimatedCircularProgress} from "react-native-circular-progress";

import {CloseIcon} from "../../react";
import {Box} from "../ui/box";
import {Center} from "../ui/center";
import {Pressable} from "../ui/pressable";
import {Text} from "../ui/text";

interface Props {
    progress: number;
    loadingSize?: DimensionValue;
    textSize?: "xs" | "2xs" | "sm";
    closeProgressIconSize?: "3xs" | "sm";
    onCancelUploading?: () => void;
}

const btnCancelStyle = tva({
    base: "absolute",
    variants: {
        size: {
            sm: "top-2 right-2",
            "3xs": "top-0.5 right-0.5"
        }
    }
});

const ImageUploadProgress: FC<Props> = ({
    progress,
    loadingSize = 20,
    textSize = "xs",
    closeProgressIconSize = "sm",
    onCancelUploading
}) => {
    const btnCancelElement = onCancelUploading ? (
        <Pressable
            className={btnCancelStyle({size: closeProgressIconSize})}
            onPress={() => {
                onCancelUploading();
            }}
        >
            <CloseIcon
                color={closeProgressIconSize === "sm" ? "blueGray.400" : "white"}
                size={closeProgressIconSize}
            />
        </Pressable>
    ) : null;

    if (process.env.IS_WEB) {
        return (
            <Box className="w-full h-full relative p-4 items-center justify-center bg-black/70">
                <Box
                    className="min-h-[auto] mb-1"
                    style={{width: loadingSize, height: loadingSize}}
                >
                    <Circle
                        percent={progress}
                        strokeColor="#fff"
                        strokeWidth={12}
                        trailColor="rgb(255, 255, 255, 0.5)"
                        trailWidth={12}
                    />
                </Box>
                <Text color="white" textAlign="center" variant={textSize}>
                    {progress}%
                </Text>
                {btnCancelElement}
            </Box>
        );
    }

    return (
        <Center
            className={`
            absolute
            w-full
            h-full
            bg-black/70
            inset-0
            border
            border-solid
            border-blueGray-200
            rounded-lg
            z-10
          `}
        >
            <AnimatedCircularProgress
                backgroundColor="#ffffff80"
                fill={progress}
                rotation={0}
                size={14}
                tintColor="white"
                width={2}
            />
            {btnCancelElement}
        </Center>
    );
};

export default memo(ImageUploadProgress);
